import { useDocItems } from '@modules/docs/providers';
import {
  SearchResultItem,
  SearchTypeEnum,
} from '@modules/global-search/shared/types';
import { useGlobalSearch } from '@modules/global-search/shared/useGlobalSearch';
import { Stack, Typography, styled } from '@mui/material';
import { Box } from '@mui/system';
import { useMemo } from 'react';
import {
  ContextItemFileType,
  ContextItemType,
  ContextItems,
} from 'src/@types/ai';
import { DocItem } from 'src/@types/docs';
import { IDriveContent } from 'src/@types/drive';
import { Loading } from 'src/components/Loading/Loading';
import { DocsExplorerItem } from 'src/components/explorer/docs/DocsExplorerItem';
import { DriveExplorerItem } from 'src/components/explorer/drive/DriveExplorerItem';
import { useLocales } from 'src/locales';

const validMimeTypes = ['image', 'text', 'application/pdf'];

type DefaultSearchComponentsProps = {
  hasContextFile: (
    type: ContextItemType,
    contextFile: ContextItemFileType,
  ) => boolean;
};

type DocsProps = DefaultSearchComponentsProps & {
  item: DocItem;
  selectDoc: (doc: DocItem) => void;
  docs: DocItem[];
};

type DriveProps = DefaultSearchComponentsProps & {
  item: IDriveContent;
  selectFile: (file: IDriveContent) => void;
};

const SearchComponents: Record<ContextItemType, any> = {
  [ContextItemType.DOC]: ({
    item,
    docs,
    selectDoc,
    hasContextFile,
  }: DocsProps) => (
    <DocsExplorerItem
      key={item._id}
      doc={item as DocItem}
      selectDoc={selectDoc}
      isSelected={(doc: DocItem) => hasContextFile(ContextItemType.DOC, doc)}
      docs={docs}
    />
  ),
  [ContextItemType.DRIVE]: ({
    item,
    selectFile,
    hasContextFile,
  }: DriveProps) => (
    <DriveExplorerItem
      key={item._id}
      driveItem={item as IDriveContent}
      selectFile={selectFile}
      isSelected={(file: IDriveContent) =>
        hasContextFile(ContextItemType.DRIVE, file)
      }
      validMimeTypes={validMimeTypes}
    />
  ),
  [ContextItemType.FILE]: () => <></>,
  [ContextItemType.IMAGE]: () => <></>,
  [ContextItemType.RECENT]: () => <></>,
};

type Props = {
  searchTerm: string;
  selectDoc: (doc: DocItem) => void;
  selectFile: (file: IDriveContent) => void;
  hasContextFile: (
    type: ContextItemType,
    contextFile: ContextItemFileType,
  ) => boolean;
};

export const AIContextSearch = ({
  searchTerm,
  selectDoc,
  selectFile,
  hasContextFile,
}: Props) => {
  const { docs } = useDocItems();
  const { translate } = useLocales();

  const { searchData, isLoading } = useGlobalSearch({
    searchTerm,
    isFlat: true,
    searchTypes: [SearchTypeEnum.DOCS, SearchTypeEnum.DRIVE],
  });

  const formattedContextResult = useMemo(() => {
    if (!searchTerm || !(searchData as SearchResultItem[])?.length) return [];

    return (searchData as SearchResultItem[]).map((item) => {
      if (item.searchType === SearchTypeEnum.DOCS) {
        return {
          type: ContextItemType.DOC,
          doc: item as DocItem,
        } as ContextItems;
      }
      return {
        type: ContextItemType.DRIVE,
        drive: item as IDriveContent,
      } as ContextItems;
    });
  }, [searchTerm, searchData]);

  if (isLoading) {
    return (
      <>
        <Loading />
        <Stack alignItems="center" py={2}>
          <Typography variant="caption" color="text.secondary">
            {translate('shared.labels.searching')}
          </Typography>
        </Stack>
      </>
    );
  }
  return (
    <StyledBox>
      {!formattedContextResult.length && (
        <Stack alignItems="center" py={2}>
          <Typography variant="caption" color="text.secondary">
            {translate('shared.not_found.no_search_results_found')}
          </Typography>
        </Stack>
      )}
      {formattedContextResult?.map((contextItem) => {
        const item = contextItem[contextItem.type]!;
        const Component = SearchComponents[contextItem.type];
        return (
          <Component
            key={item._id}
            item={item}
            selectDoc={selectDoc}
            selectFile={selectFile}
            docs={docs}
            hasContextFile={hasContextFile}
          />
        );
      })}
    </StyledBox>
  );
};

const StyledBox = styled(Box)(({ theme }) => ({
  overflow: 'hidden',
  '& .MuiMenuItem-root': {
    marginBottom: theme.spacing(0),
    paddingLeft: theme.spacing(1),
    overflow: 'hidden',
    '& .MuiListItemIcon-root': {
      minWidth: 26,
    },
  },
}));
